import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Newspaper, Download, ShieldCheck, Lock, FileText, Image as ImageIcon } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Press() {
  const { user } = useAuth();
  const [kit, setKit] = useState(null);
  const [denied, setDenied] = useState(false);
  const [form, setForm] = useState({ company: "", email: "" });

  useEffect(() => {
    if (!user) return;
    api.get("/press/kit").then((r) => { setKit(r.data); setDenied(false); }).catch((e) => {
      if (e.response?.status === 403) setDenied(true);
    });
  }, [user]);

  const request = async (e) => {
    e.preventDefault();
    try {
      await api.post("/press/request", { company: form.company, email: form.email });
      toast.success("Accreditation requested", { description: "Our media team will review and grant access shortly." });
      setForm({ company: "", email: "" });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Please try again"); }
  };

  const accredited = kit && !denied;

  return (
    <div className="section py-12 sm:py-16" data-testid="press-page">
      <p className="kicker mb-2 flex items-center gap-2"><Newspaper className="h-4 w-4" /> Media & Press</p>
      <h1 className="text-4xl sm:text-5xl lg:text-6xl text-amber-50">Press Portal</h1>
      <p className="mt-3 max-w-2xl text-amber-100/60">Accredited journalists get official candidate bios, high-resolution portraits and the latest press releases for the continental edition.</p>

      {accredited ? (
        <div data-testid="press-kit">
          <div className="mt-8 inline-flex items-center gap-2 rounded-full border border-emerald-accent/40 bg-emerald-accent/10 px-4 py-2 text-sm text-emerald-accent">
            <ShieldCheck className="h-4 w-4" /> Accredited — full media kit unlocked
          </div>

          <p className="kicker mb-6 mt-12 flex items-center gap-2"><FileText className="h-4 w-4" /> Press Releases</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {(kit.releases || []).map((n) => (
              <div key={n.id} className="card-lux p-6">
                <p className="text-[0.6rem] uppercase tracking-wider text-gold/70">{n.category}</p>
                <h3 className="mt-2 font-serif text-lg text-amber-50">{n.title}</h3>
                <p className="mt-2 text-sm text-amber-100/60 line-clamp-3">{n.excerpt}</p>
                <button onClick={() => { const blob = new Blob([`${n.title}\n\n${n.excerpt}`], { type: "text/plain" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${n.slug || "release"}.txt`; a.click(); }}
                  data-testid={`download-release-${n.id}`} className="mt-4 inline-flex items-center gap-2 text-xs text-gold hover:underline"><Download className="h-3.5 w-3.5" /> Download release</button>
              </div>
            ))}
            {(kit.releases || []).length === 0 && <p className="text-sm text-muted-foreground">No releases published yet.</p>}
          </div>

          <p className="kicker mb-6 mt-14 flex items-center gap-2"><ImageIcon className="h-4 w-4" /> Candidate Media Bios</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {(kit.bios || []).map((b) => (
              <div key={b.slug} className="card-lux overflow-hidden" data-testid={`press-bio-${b.slug}`}>
                <div className="aspect-[3/4] overflow-hidden bg-obsidian-600">
                  {b.portrait_url && <img src={b.portrait_url} alt={b.name} loading="lazy" className="h-full w-full object-cover" />}
                </div>
                <div className="p-4">
                  <p className="text-sm text-amber-50">{b.name}</p>
                  <p className="text-xs text-muted-foreground">{b.delegation_name}</p>
                  {b.advocacy && <p className="mt-1 text-[0.65rem] uppercase tracking-wider text-gold/70">{b.advocacy}</p>}
                  <a href={b.portrait_url} download target="_blank" rel="noreferrer" data-testid={`download-portrait-${b.slug}`} className="mt-3 inline-flex items-center gap-1.5 text-xs text-gold hover:underline"><Download className="h-3 w-3" /> Portrait</a>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          <div className="glass rounded-2xl p-8" data-testid="press-request-form">
            <h2 className="flex items-center gap-2 text-3xl text-amber-50"><Lock className="h-6 w-6 text-gold" /> Request Accreditation</h2>
            <p className="mt-2 text-amber-100/60">
              {user ? (denied ? "Your account isn't accredited yet. Submit your outlet details for review." : "Submit your media outlet details to request access.")
                : "Please sign in first, then request press accreditation."}
            </p>
            <form onSubmit={request} className="mt-6 grid gap-4">
              <input required placeholder="Media outlet / publication" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} data-testid="press-company" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold" />
              <input required type="email" placeholder="Work email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="press-email" className="rounded-lg border border-gold/25 bg-obsidian px-4 py-3 text-sm text-amber-50 outline-none focus:border-gold" />
              <button className="btn-gold" data-testid="press-submit">Request Accreditation</button>
            </form>
          </div>
          <div className="card-lux p-8">
            <p className="kicker mb-4">What accredited press receive</p>
            <ul className="space-y-3 text-sm text-amber-100/70">
              <li className="flex gap-3"><ShieldCheck className="h-5 w-5 shrink-0 text-gold" /> Official candidate bios & high-resolution portraits</li>
              <li className="flex gap-3"><ShieldCheck className="h-5 w-5 shrink-0 text-gold" /> Latest press releases and continental announcements</li>
              <li className="flex gap-3"><ShieldCheck className="h-5 w-5 shrink-0 text-gold" /> Priority access to livestream and event coverage</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
